/**
 * Right-side detail panel for ONE Meta ad set (#6210) — the Meta sibling of Google's ad-group page
 * at ``routes/.../google/$campaignslug/_slug/ad/$adId.tsx``.
 *
 * Jeff's parity model: Campaign → Ad Set → Ad maps onto Google's Campaign → Ad Group → Ad, so the
 * ad set gets the ad-group tab anatomy with Meta-native semantics — Overview, then the three copy
 * tabs in Meta's precedence order (primary text ranks above headline), then Creatives and
 * Placements. No negatives/keywords (no Meta analog); each ad in the set is one ANGLE (its
 * creative + its 3–5 hook variants per copy field).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Eye, Plus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/glitch-ui/spinner';
import { EditableField } from '@/components/glitch-ui/editable-field';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { CampaignInfoForm, type CampaignInfoRow } from '@/components/campaign/detail/info-form';
import { MetaAdVisual } from '@/components/campaign/meta/meta-ad-detail-panel';
import { MetaAdPreviewModal } from '@/components/campaign/meta/meta-ad-preview-modal';
import { derivePlacementsChecked } from '@/components/campaign/meta/meta-detail-adapter';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useToast } from '@/hooks/useToast';
import { selectedMetaAdSetTabAtom } from '@/store';
import {
  metaCampaignDetailQueryOptions,
  useUpdateMetaAdCopyMutation,
  useUpdateMetaAdSetNameMutation,
} from '@/lib/query-options';
import { META_PLACEMENT_CATALOG } from '@/lib/types';
import type { MetaAdResponse, MetaAdSetResponse, MetaCampaignDetailResponse } from '@/lib/types';

type MetaAdSetTabId = 'overview' | 'primary-text' | 'headlines' | 'descriptions' | 'creatives' | 'placements';

// Meta's field precedence puts primary text ahead of headline — hence the tab order (Jeff's
// parity review). Creatives is the deliberate name (not "Visuals").
const TABS: { id: MetaAdSetTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'primary-text', label: 'Primary Text' },
  { id: 'headlines', label: 'Headlines' },
  { id: 'descriptions', label: 'Descriptions' },
  { id: 'creatives', label: 'Creatives' },
  { id: 'placements', label: 'Placements' },
];

/** Count every angle's hooks for one copy field — the Overview's "N primary texts" figures. */
function countHooks(adSet: MetaAdSetResponse, field: 'primary_texts' | 'headlines' | 'descriptions'): number {
  const scalarOf = { primary_texts: 'primary_text', headlines: 'headline', descriptions: 'description' } as const;
  return adSet.ads.reduce((sum, ad) => {
    const hooks = ad[field];
    if (hooks && hooks.length > 0) return sum + hooks.length;
    return sum + (ad[scalarOf[field]] ? 1 : 0);
  }, 0);
}

/** Google's ad-group Overview grid with the ad set's fields: copy counts link the copy tabs' contents. */
function metaAdSetInfoFields(adSet: MetaAdSetResponse, campaign: MetaCampaignDetailResponse): CampaignInfoRow[] {
  const landingPage = (() => {
    const url = adSet.ads[0]?.destination_url ?? campaign.offer_url;
    try {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname === '/' ? '' : parsed.pathname}`;
    } catch {
      return url;
    }
  })();
  return [
    [
      { label: 'Angles', value: String(adSet.ads.length) },
      { label: 'Status', value: adSet.status },
    ],
    [
      { label: 'Primary Texts', value: String(countHooks(adSet, 'primary_texts')) },
      { label: 'Headlines', value: String(countHooks(adSet, 'headlines')) },
      { label: 'Descriptions', value: String(countHooks(adSet, 'descriptions')) },
    ],
    { label: 'Landing Page URL', value: landingPage },
  ];
}

export function MetaAdSetDetailPanel({ slug, adSetId }: { slug: string; adSetId: number }) {
  const { data, isLoading, isError } = useQuery(metaCampaignDetailQueryOptions(slug));
  const adSet = data?.ad_sets.find((candidate) => candidate.id === adSetId);

  // Hash-carried tab like every other tabbed page (Google's ad page pattern): shareable URLs,
  // back/forward restores the tab, and the atom restores it on sidebar re-entry.
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedMetaAdSetTabAtom,
    TABS.map((tab) => tab.id),
    'overview',
    (tabId, replace) =>
      navigate({
        to: '/campaigns/meta/$metaCampaignSlug/adset/$adSetId',
        params: { metaCampaignSlug: slug, adSetId: String(adSetId) },
        hash: tabId,
        replace,
      }),
    location.hash,
  );

  // Dirty aggregation across the per-angle hook editors: the blocker warns while ANY angle has
  // staged hooks; Save All / Discard fan out through the registered actions.
  const [dirtyAdIds, setDirtyAdIds] = useState<Set<number>>(new Set());
  const actionsRef = useRef(new Map<number, { save: () => Promise<boolean>; reset: () => void }>());
  const handleDirtyChange = useCallback((adId: number, isDirty: boolean) => {
    setDirtyAdIds((prev) => {
      if (prev.has(adId) === isDirty) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(adId);
      else next.delete(adId);
      return next;
    });
  }, []);
  const registerActions = useCallback(
    (adId: number, actions: { save: () => Promise<boolean>; reset: () => void } | null) => {
      if (actions) actionsRef.current.set(adId, actions);
      else actionsRef.current.delete(adId);
    },
    [],
  );
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: dirtyAdIds.size > 0,
    onSave: async () => {
      const results = await Promise.all(
        [...dirtyAdIds].map((adId) => actionsRef.current.get(adId)?.save() ?? Promise.resolve(true)),
      );
      if (results.some((saved) => !saved)) {
        throw new Error('Some hooks were not saved. Fix the marked fields or discard the changes.');
      }
    },
    onDiscard: () => {
      for (const adId of dirtyAdIds) actionsRef.current.get(adId)?.reset();
    },
    isSaving: false,
    contextName: adSet?.name,
  });

  const { toast } = useToast();
  const renameMutation = useUpdateMetaAdSetNameMutation(slug);
  // Controlled draft for the EditableField: resyncs when the ad set (or its saved name) changes;
  // commit happens on blur — blank restores the saved name (the #5981 restore-on-blank pattern).
  const [nameDraft, setNameDraft] = useState('');
  useEffect(() => {
    setNameDraft(adSet?.name ?? '');
  }, [adSet?.id, adSet?.name]);
  const handleNameBlur = async (value: string) => {
    if (!adSet) return;
    const trimmed = value.trim();
    if (!trimmed || trimmed === adSet.name) {
      setNameDraft(adSet.name);
      return;
    }
    try {
      await renameMutation.mutateAsync({ adSetId: adSet.id, name: trimmed });
    } catch (error) {
      setNameDraft(adSet.name);
      toast({
        title: 'Could Not Rename Ad Set',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div>
      <div className="flex items-end gap-1">
        {TABS.map((tab) => (
          <button key={tab.id} className={tabButtonClass(activeTab === tab.id)} onClick={() => handleTabChange(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>
      <Card className="rounded-lg rounded-tl-none border bg-white">
        <CardContent className="p-6">
          {isLoading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
              <Spinner className="w-4 h-4" /> Loading Meta ad set…
            </div>
          ) : isError || !data || !adSet ? (
            <div className="flex items-start gap-3 rounded-lg border-l-4 border-[#FCA5A5] bg-[#FEE2E2] p-4 text-[#B91C1C]">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Could not load this Meta ad set.</p>
                <p className="text-xs">It may have been removed. Go back to the campaign and refresh.</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-1 font-semibold text-slate-800">Ad Set Name</h3>
                    {/* Hover-to-edit like Google's ad-group name; persists on blur via the rename
                        endpoint (Meta-side first, then the row). */}
                    <EditableField
                      value={nameDraft}
                      onChange={setNameDraft}
                      onBlur={handleNameBlur}
                      maxLength={255}
                      hideBullet
                      disabled={renameMutation.isPending}
                    />
                  </div>
                  <CampaignInfoForm fields={metaAdSetInfoFields(adSet, data)} />
                </div>
              )}
              {activeTab === 'primary-text' && (
                <MetaAdSetCopyTab adSet={adSet} field="primary_texts" slug={slug} onDirtyChange={handleDirtyChange} registerActions={registerActions} />
              )}
              {activeTab === 'headlines' && (
                <MetaAdSetCopyTab adSet={adSet} field="headlines" slug={slug} onDirtyChange={handleDirtyChange} registerActions={registerActions} />
              )}
              {activeTab === 'descriptions' && (
                <MetaAdSetCopyTab adSet={adSet} field="descriptions" slug={slug} onDirtyChange={handleDirtyChange} registerActions={registerActions} />
              )}
              {activeTab === 'creatives' && <MetaAdSetCreativesTab adSet={adSet} slug={slug} />}
              {activeTab === 'placements' && <MetaAdSetPlacementsTab campaign={data} slug={slug} />}
            </>
          )}
        </CardContent>
      </Card>
      {UnsavedChangesModal}
    </div>
  );
}

/**
 * The Creatives tab (#6210): one card per angle — the ad's name linking to its ad page, its
 * creative (thumbnail + metadata), and its lead hook as the caption so the visual reads in the
 * context of its copy. "Creatives", not "Visuals" — Jeff's explicit naming.
 */
function MetaAdSetCreativesTab({ adSet, slug }: { adSet: MetaAdSetResponse; slug: string }) {
  const navigate = useNavigate();
  // The eye icon (#6213): Meta's own rendering of the ad, per format, in a modal.
  const [previewAd, setPreviewAd] = useState<MetaAdResponse | null>(null);
  if (adSet.ads.length === 0) {
    return <p className="py-2 text-sm text-gray-500">No ads in this ad set yet.</p>;
  }
  return (
    <div className="grid grid-cols-2 gap-6">
      {previewAd && <MetaAdPreviewModal adId={previewAd.id} adName={previewAd.name} onClose={() => setPreviewAd(null)} />}
      {adSet.ads.map((ad) => (
        <div key={ad.id}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: '/campaigns/meta/$metaCampaignSlug/ad/$adId',
                  params: { metaCampaignSlug: slug, adId: String(ad.id) },
                })
              }
              className="min-w-0 truncate text-sm font-semibold text-gray-900 hover:text-[#290BD9] hover:underline"
            >
              {ad.name}
            </button>
            <button
              type="button"
              onClick={() => setPreviewAd(ad)}
              aria-label={`Preview ${ad.name}`}
              title="Preview on Meta"
              className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-[#290BD9]"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
          <MetaAdVisual assetId={ad.creative_asset_id} alt={ad.name} />
          {(ad.headlines?.[0] ?? ad.headline) && (
            <p className="mt-2 truncate text-sm text-gray-500" title={ad.headlines?.[0] ?? ad.headline ?? ''}>
              {ad.headlines?.[0] ?? ad.headline}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The Placements tab (#6210): a READ-ONLY mirror of the campaign-level placement selection.
 * Placements are campaign-scoped today (``MetaCampaign.target_placements``, written into every ad
 * set's targeting spec) and edited on the campaign's Targeting tab — per-ad-set editing arrives
 * with add-ad-set (#6211), where ad sets first diverge.
 */
function MetaAdSetPlacementsTab({ campaign, slug }: { campaign: MetaCampaignDetailResponse; slug: string }) {
  const navigate = useNavigate();
  const saved = campaign.target_placements ?? null;
  const isAutomatic = !saved || saved.publisher_platforms.length === 0;
  const { checkedPlatforms, checkedPositions } = derivePlacementsChecked(saved);
  return (
    <div className="space-y-4">
      {isAutomatic ? (
        <p className="text-sm text-gray-900">
          Advantage+ placements: Meta automatically serves this ad set across all placements.
        </p>
      ) : (
        <dl className="space-y-2">
          {META_PLACEMENT_CATALOG.filter((entry) => checkedPlatforms.has(entry.platform)).map((entry) => {
            const positions = entry.positions.filter((position) =>
              checkedPositions[entry.platform]?.has(position.value),
            );
            return (
              <div key={entry.platform} className="flex items-start justify-between gap-3 text-sm">
                <dt className="flex-shrink-0 font-medium text-gray-900">{entry.label}</dt>
                <dd className="text-right text-gray-500">
                  {positions.length > 0 ? positions.map((position) => position.label).join(', ') : 'All positions'}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      <p className="text-sm text-gray-500">
        Placements apply to the whole campaign.{' '}
        <button
          type="button"
          onClick={() =>
            navigate({
              to: '/campaigns/meta/$metaCampaignSlug',
              params: { metaCampaignSlug: slug },
              hash: 'targeting',
            })
          }
          className="text-[#290BD9] hover:text-[#2309C5] hover:underline"
        >
          Edit them in Targeting
        </button>
      </p>
    </div>
  );
}

type CopyField = 'primary_texts' | 'headlines' | 'descriptions';

const SCALAR_OF: Record<CopyField, 'primary_text' | 'headline' | 'description'> = {
  primary_texts: 'primary_text',
  headlines: 'headline',
  descriptions: 'description',
};

// asset_feed_spec caps each text list at 5 (META_MAX_COPY_VARIANTS backend-side).
const MAX_HOOKS = 5;
// Headline/description hooks mirror the 255-char columns; primary text is unbounded.
const FIELD_MAX_LENGTH: Record<CopyField, number | undefined> = {
  primary_texts: undefined,
  headlines: 255,
  descriptions: 255,
};

/** An ad's current hook list for one field: the stored list, else its scalar as the only hook. */
function hooksOf(ad: MetaAdResponse, field: CopyField): string[] {
  const stored = ad[field];
  if (stored && stored.length > 0) return stored;
  const scalar = ad[SCALAR_OF[field]];
  return scalar ? [scalar] : [];
}

/**
 * One angle's hooks for one copy field (#6210): individually editable rows, add/remove within
 * 1–{MAX_HOOKS}, staged until the per-angle Save — which replaces the field's whole list through
 * the copy PATCH (plural form). Blank rows are marked on save (tell-on-click), not silently
 * dropped. Registers its dirty/save/reset with the panel so the unsaved-changes blocker can
 * aggregate across angles.
 */
function MetaAngleHooksEditor({
  ad,
  field,
  slug,
  onDirtyChange,
  registerActions,
}: {
  ad: MetaAdResponse;
  field: CopyField;
  slug: string;
  onDirtyChange: (adId: number, isDirty: boolean) => void;
  registerActions: (adId: number, actions: { save: () => Promise<boolean>; reset: () => void } | null) => void;
}) {
  const saved = hooksOf(ad, field);
  const [draft, setDraft] = useState<string[]>(saved);
  const [errors, setErrors] = useState<Set<number>>(new Set());
  const copyMutation = useUpdateMetaAdCopyMutation(slug);

  // Resync when the ad (or its saved hooks) changes — the same key discipline as the ad page's
  // copy editor (#6163 P0-5).
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(JSON.parse(savedKey));
    setErrors(new Set());
     
  }, [ad.id, savedKey]);

  const isDirty = JSON.stringify(draft) !== savedKey;
  useEffect(() => {
    onDirtyChange(ad.id, isDirty);
    return () => onDirtyChange(ad.id, false);
  }, [ad.id, isDirty, onDirtyChange]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    const blank = new Set(draft.flatMap((hook, index) => (hook.trim() ? [] : [index])));
    if (blank.size > 0) {
      setErrors(blank);
      return false;
    }
    setErrors(new Set());
    try {
      await copyMutation.mutateAsync({ adId: ad.id, body: { [field]: draft.map((hook) => hook.trim()) } });
      return true;
    } catch {
      // The mutation's error lands in the row-level message below; keep the draft for a retry.
      return false;
    }
  }, [ad.id, copyMutation, draft, field]);

  const handleReset = useCallback(() => {
    setDraft(JSON.parse(savedKey));
    setErrors(new Set());
  }, [savedKey]);

  useEffect(() => {
    registerActions(ad.id, { save: handleSave, reset: handleReset });
    return () => registerActions(ad.id, null);
  }, [ad.id, handleSave, handleReset, registerActions]);

  return (
    <div className="space-y-1">
      {draft.map((hook, index) => (
        <EditableField
          key={index}
          value={hook}
          onChange={(value) => {
            setDraft((prev) => prev.map((existing, i) => (i === index ? value : existing)));
            setErrors((prev) => {
              if (!prev.has(index)) return prev;
              const next = new Set(prev);
              next.delete(index);
              return next;
            });
          }}
          onDelete={
            draft.length > 1
              ? () => setDraft((prev) => prev.filter((_, i) => i !== index))
              : undefined
          }
          deleteLabel="Remove hook"
          maxLength={FIELD_MAX_LENGTH[field]}
          errorMessage={errors.has(index) ? 'Required' : null}
          disabled={copyMutation.isPending}
        />
      ))}
      <div className="flex items-center justify-between pt-1">
        {draft.length < MAX_HOOKS ? (
          <button
            type="button"
            onClick={() => setDraft((prev) => [...prev, ''])}
            className="inline-flex items-center gap-1 text-sm text-[#290BD9] hover:text-[#2309C5]"
            disabled={copyMutation.isPending}
          >
            <Plus className="h-3.5 w-3.5" /> Add hook
          </button>
        ) : (
          <span className="text-xs text-gray-500">Meta rotates up to {MAX_HOOKS} hooks per field.</span>
        )}
        {isDirty && (
          <SaveChangesButton className="h-8" onClick={handleSave} saving={copyMutation.isPending} />
        )}
      </div>
      {copyMutation.isError && (
        <p className="text-xs text-[#B91C1C]">
          {copyMutation.error instanceof Error ? copyMutation.error.message : 'The hooks were not saved.'}
        </p>
      )}
    </div>
  );
}

/** One copy tab (#6210): every angle in the set with its editable hook list for ``field``. */
function MetaAdSetCopyTab({
  adSet,
  field,
  slug,
  onDirtyChange,
  registerActions,
}: {
  adSet: MetaAdSetResponse;
  field: CopyField;
  slug: string;
  onDirtyChange: (adId: number, isDirty: boolean) => void;
  registerActions: (adId: number, actions: { save: () => Promise<boolean>; reset: () => void } | null) => void;
}) {
  const navigate = useNavigate();
  if (adSet.ads.length === 0) {
    return <p className="py-2 text-sm text-gray-500">No ads in this ad set yet.</p>;
  }
  return (
    <div className="space-y-6">
      {adSet.ads.map((ad) => (
        <div key={ad.id}>
          {/* The angle header: each ad IS one angle (its creative + its hooks). Links to the ad
              page, mirroring how Google's headline tabs link into their ad entities. */}
          <button
            type="button"
            onClick={() =>
              navigate({
                to: '/campaigns/meta/$metaCampaignSlug/ad/$adId',
                params: { metaCampaignSlug: slug, adId: String(ad.id) },
              })
            }
            className="mb-2 text-sm font-semibold text-gray-900 hover:text-[#290BD9] hover:underline"
          >
            {ad.name}
          </button>
          <MetaAngleHooksEditor
            ad={ad}
            field={field}
            slug={slug}
            onDirtyChange={onDirtyChange}
            registerActions={registerActions}
          />
        </div>
      ))}
    </div>
  );
}
